import React, { useState, useEffect, useRef } from "react";
import { backendURL } from "../Globals";
import { SearchOutlined } from "@ant-design/icons";
import { Button, Input, Space, Table, Popconfirm, notification } from "antd";
import Cookies from "js-cookie"; // Para manejar las cookies de autenticación

const ApprovedComponent = () => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [articles, setArticles] = useState([]);
  const [editingArticle, setEditingArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  const searchInput = useRef(null);

  useEffect(() => {
    getArticles();
  }, []);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Buscar ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button type="primary" onClick={() => handleSearch(selectedKeys, confirm, dataIndex)} icon={<SearchOutlined />} size="small">
            Buscar
          </Button>
          <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small">
            Resetear
          </Button>
          <Button type="link" size="small" onClick={() => close()}>Cerrar</Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />,
    onFilter: (value, record) => record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
  });

  const getArticles = async () => {
    setLoading(true);
    const apiKey = Cookies.get("apiKey") || localStorage.getItem("apiKey");
    if (!apiKey) {
      notification.error({ message: "Error de autenticación", description: "No se encontró una API Key. Inicia sesión nuevamente." });
      setLoading(false);
      return;
    }
    try {
      let response = await fetch(`${backendURL}/articles`, {
        headers: { "Authorization": `Bearer ${apiKey}`, "Content-Type": "application/json" }
      });
      if (response.status === 401) {
        notification.error({ message: "Acceso no autorizado", description: "Tu sesión ha expirado. Por favor, inicia sesión nuevamente." });
        setLoading(false);
        return;
      }
      let jsonData = await response.json();
      setArticles(jsonData);
    } catch (error) {
      console.error("Error fetching articles:", error);
      notification.error({ message: "Error al obtener artículos", description: error.message });
    } finally {
      setLoading(false);
    }
  };

  const clickModify = (record) => {
    setEditingId(record.id);
    setEditingArticle(record);
  };

  const saveModification = async (id) => {
    const apiKey = Cookies.get("apiKey") || localStorage.getItem("apiKey");
    try {
      let response = await fetch(`${backendURL}/articles/${id}`, {
        method: "PUT",
        headers: { "Authorization": `Bearer ${apiKey}`, "Content-Type": "application/json" },
        body: JSON.stringify(editingArticle),
      });
      if (response.ok) {
        setEditingId(null);
        setArticles((prevArticles) => prevArticles.map((article) => (article.id === id ? { ...article, ...editingArticle } : article)));
        notification.success({ message: "Artículo Modificado", description: "El artículo se modificó con éxito." });
      } else {
        let jsonData = await response.json();
        notification.error({ message: jsonData.error });
      }
    } catch (error) {
      console.error("Error modifying article:", error);
      notification.error({ message: "Error al modificar artículo", description: error.message });
    }
  };

  const clickDelete = async (id) => {
    const apiKey = Cookies.get("apiKey") || localStorage.getItem("apiKey");
    try {
      let response = await fetch(`${backendURL}/articles/${id}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${apiKey}` },
      });
      if (response.ok) {
        setArticles((prevArticles) => prevArticles.filter((article) => article.id !== id));
        notification.success({ message: "Artículo Eliminado", description: "El artículo fue eliminado con éxito." });
      } else {
        let jsonData = await response.json();
        notification.error({ message: jsonData.error });
      }
    } catch (error) {
      console.error("Error deleting article:", error);
      notification.error({ message: "Error al eliminar artículo", description: error.message });
    }
  };

  const columns = [
    {
      title: "Título",
      dataIndex: "titulo",
      key: "titulo",
      ...getColumnSearchProps("titulo"),
      render: (text, record) =>
        editingId === record.id ? (
          <Input
            value={editingArticle.titulo}
            onChange={(e) =>
              setEditingArticle({ ...editingArticle, titulo: e.target.value })
            }
          />
        ) : (
          text
        ),
    },
    {
      title: "DOI",
      dataIndex: "doi",
      key: "doi",
      ...getColumnSearchProps("doi"),
      render: (text, record) =>
        editingId === record.id ? (
          <Input
            value={editingArticle.doi}
            onChange={(e) =>
              setEditingArticle({ ...editingArticle, doi: e.target.value })
            }
          />
        ) : (
          text
        ),
    },
    {
      title: "Autor",
      dataIndex: "autorLikePortal",
      key: "autorLikePortal",
      ...getColumnSearchProps("autorLikePortal"),
      render: (text, record) =>
        editingId === record.id ? (
          <Input
            value={editingArticle.autorLikePortal}
            onChange={(e) =>
              setEditingArticle({ ...editingArticle, autorLikePortal: e.target.value })
            }
          />
        ) : (
          text
        ),
    },
    {
      title: "Departamento",
      dataIndex: "departamento",
      key: "departamento",
      ...getColumnSearchProps("departamento"),
    },
    {
      title: "Editor",
      dataIndex: "editor",
      key: "editor",
      ...getColumnSearchProps("editor"),
    },
    {
      title: "Acciones",
      key: "acciones",
      render: (text, record) => (
        <Space>
          {editingId === record.id ? (
            <Button type="primary" onClick={() => saveModification(record.id)}>
              Guardar
            </Button>
          ) : (
            <Button onClick={() => clickModify(record)}>Modificar</Button>
          )}
          <Popconfirm title="¿Seguro que deseas eliminar?" onConfirm={() => clickDelete(record.id)}>
            <Button danger>Eliminar</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return <Table columns={columns} dataSource={articles} rowKey="id" loading={loading} pagination={{ pageSize: 10 }} style={{ margin: "20px" }} />;
};

export default ApprovedComponent;
